import type { Game } from '@aco/shared';

/**
 * Returns the two players who threw this round for PPR scoring.
 *
 * Singles: always Player A (index 0) from each team — they throw every round.
 *
 * Doubles: players alternate ends each round so that stats are credited to the
 * correct thrower:
 *   - Odd  rounds (1, 3, 5 …): Player A from each team (index 0)
 *   - Even rounds (2, 4, 6 …): Player B from each team (index 1)
 *
 * @param game        The full game object.
 * @param _entryTeam  Unused — kept for API compatibility.
 * @param roundNumber Override the round number to use (defaults to game.currentRound).
 *                    Pass the specific round when correcting historical rounds.
 *
 * Convention: index 0 = team1's throwing player, index 1 = team2's throwing player.
 * teamColor is the scoreboard square color for their team.
 */
export function getPprPlayers(
  game: Game,
  _entryTeam: 'team1' | 'team2',
  roundNumber?: number,
): Array<{ id: string; name: string; teamColor: string }> {
  const team1Color = game.team1.side === 'left' ? '#1a3a6b' : '#c0392b';
  const team2Color = game.team2.side === 'left' ? '#1a3a6b' : '#c0392b';

  // Doubles: 2 players per team — alternate active player each round.
  // Odd round → index 0 (Player A), even round → index 1 (Player B).
  const round = roundNumber ?? game.currentRound;
  const isDoubles =
    game.team1.players.length >= 2 && game.team2.players.length >= 2;
  const playerIndex = isDoubles && round % 2 === 0 ? 1 : 0;

  return [
    {
      id: game.team1.players[playerIndex]?.id ?? `team1-p${playerIndex + 1}`,
      name:
        game.team1.players[playerIndex]?.name ??
        game.team1.players[0]?.name ??
        game.team1.name,
      teamColor: team1Color,
    },
    {
      id: game.team2.players[playerIndex]?.id ?? `team2-p${playerIndex + 1}`,
      name:
        game.team2.players[playerIndex]?.name ??
        game.team2.players[0]?.name ??
        game.team2.name,
      teamColor: team2Color,
    },
  ];
}
